import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../../api/cliente';
import { conexoes as apiConexoes } from '../../api/endpoints';
import type { Conexao, DadosConexao, ResultadoTesteConexao, TipoAmbiente } from '../../api/tipos';
import { Aviso } from '../../components/Aviso';
import { Botao } from '../../components/Botao';
import { CaixaMarcar, Campo, Selecao } from '../../components/Campo';
import { Dialogo } from '../../components/Dialogo';
import { Carregando, Pagina } from '../../components/Pagina';
import { CHAVE_SESSAO } from '../../hooks/useSessao';
import { formatarDataHora, formatarInteiro } from '../../lib/formatos';

const CHAVE = ['admin', 'conexoes'];

function SeloTipo({ tipo }: { tipo: TipoAmbiente }) {
  return (
    <span className={clsx('rounded-campo px-1.5 py-0.5 text-[11px] font-semibold', tipo === 'PRODUCAO' ? 'bg-erro text-white' : 'bg-aviso-borda text-tinta')}>
      {tipo === 'PRODUCAO' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}
    </span>
  );
}

/** Ambientes do CIGAM (produção/homologação) e qual está ativo (SPEC §4.5 e §8.8). */
export function Conexoes() {
  const cliente = useQueryClient();
  const { data, isPending, error } = useQuery({ queryKey: CHAVE, queryFn: apiConexoes.listar });
  const [editando, setEditando] = useState<Conexao | 'nova' | null>(null);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  function recarregar() {
    void cliente.invalidateQueries({ queryKey: CHAVE });
    // O selo do topo e as listas lidas do CIGAM mudam junto com o ambiente.
    void cliente.invalidateQueries({ queryKey: CHAVE_SESSAO });
    void cliente.invalidateQueries({ queryKey: ['tabelas'] });
    void cliente.invalidateQueries({ queryKey: ['custos'] });
  }

  const ativar = useMutation({
    mutationFn: (c: Conexao) => apiConexoes.ativar(c.id),
    onSuccess: (_r, c) => {
      setMensagem({ tipo: 'ok', texto: `Ambiente "${c.nomeAmbiente}" ativado. Todas as leituras e publicações passam a usar ${c.banco}.` });
      recarregar();
    },
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível ativar.' }),
  });
  const excluir = useMutation({
    mutationFn: (c: Conexao) => apiConexoes.excluir(c.id),
    onSuccess: () => {
      setMensagem({ tipo: 'ok', texto: 'Ambiente excluído.' });
      recarregar();
    },
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível excluir.' }),
  });

  return (
    <Pagina
      titulo="Conexão com o CIGAM"
      contexto="Ambientes cadastrados. Só um fica ativo: é dele que o sistema lê materiais e preços e nele que publica."
      acoes={
        <Botao variante="principal" onClick={() => setEditando('nova')}>
          <Plus aria-hidden className="size-4" /> Novo ambiente
        </Botao>
      }
    >
      <div className="flex max-w-4xl flex-col gap-4">
        {data && (
          <Aviso tipo={data.modoEscrita === 'live' ? 'aviso' : 'info'} titulo={data.modoEscrita === 'live' ? 'Gravação real ligada' : 'Modo simulação'}>
            {data.modoEscrita === 'live'
              ? 'CIGAM_WRITE_MODE=live: a publicação grava os preços no ambiente ativo.'
              : 'CIGAM_WRITE_MODE=dryrun: a publicação roda e é desfeita. Para gravar, mude para live no .env do servidor.'}
            {data.ativo?.origem === 'env' && ' A conexão ativa vem do .env (só em desenvolvimento).'}
          </Aviso>
        )}
        {mensagem && <Aviso tipo={mensagem.tipo}>{mensagem.texto}</Aviso>}
        {isPending ? (
          <Carregando />
        ) : error ? (
          <Aviso tipo="erro">{error.message}</Aviso>
        ) : data.conexoes.length === 0 ? (
          <Aviso tipo="info">Nenhum ambiente cadastrado. O primeiro cadastrado já fica ativo.</Aviso>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.conexoes.map((c) => (
              <li key={c.id} className={clsx('flex flex-wrap items-center gap-3 rounded-painel border bg-superficie px-4 py-3', c.ativo ? 'border-acao ring-1 ring-acao/30' : 'border-linha')}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-semibold">
                    {c.nomeAmbiente} <SeloTipo tipo={c.tipo} />
                    {c.ativo && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-acao-escura">
                        <Check aria-hidden className="size-3.5" /> ativo
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-[13px] text-tinta-2">
                    {c.usuario}@{c.host}:{c.porta} / {c.banco}
                  </p>
                  <p className="text-[12px] text-tinta-2">
                    Alterado em {formatarDataHora(c.atualizadoEm)}
                    {c.atualizadoPor && ` por ${c.atualizadoPor}`}
                  </p>
                </div>
                <div className="flex gap-1">
                  {!c.ativo && (
                    <Botao tamanho="compacto" variante="secundario" carregando={ativar.isPending && ativar.variables?.id === c.id} onClick={() => ativar.mutate(c)}>
                      Ativar
                    </Botao>
                  )}
                  <Botao tamanho="compacto" variante="discreto" onClick={() => setEditando(c)}>
                    <Pencil aria-hidden className="size-3.5" /> Editar
                  </Botao>
                  {!c.ativo && (
                    <Botao tamanho="compacto" variante="discreto" aria-label={`Excluir ${c.nomeAmbiente}`} onClick={() => excluir.mutate(c)}>
                      <Trash2 aria-hidden className="size-3.5" />
                    </Botao>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialogo aberto={editando !== null} titulo={editando === 'nova' ? 'Novo ambiente' : 'Editar ambiente'} largura="max-w-xl" aoFechar={() => setEditando(null)}>
        {editando !== null && (
          <FormConexao
            conexao={editando === 'nova' ? null : editando}
            aoConcluir={(texto) => {
              setEditando(null);
              setMensagem({ tipo: 'ok', texto });
              recarregar();
            }}
          />
        )}
      </Dialogo>
    </Pagina>
  );
}

function FormConexao({ conexao, aoConcluir }: { conexao: Conexao | null; aoConcluir: (mensagem: string) => void }) {
  const [dados, setDados] = useState<DadosConexao>(() => ({
    nomeAmbiente: conexao?.nomeAmbiente ?? '',
    tipo: conexao?.tipo ?? 'PRODUCAO',
    host: conexao?.host ?? 'wts.mfti.com.br',
    porta: conexao?.porta ?? 11433,
    banco: conexao?.banco ?? '',
    usuario: conexao?.usuario ?? '',
    senha: '',
    encrypt: conexao?.encrypt ?? false,
    trustServerCertificate: conexao?.trustServerCertificate ?? true,
  }));
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [teste, setTeste] = useState<ResultadoTesteConexao | null>(null);

  const mudar = <K extends keyof DadosConexao>(k: K, v: DadosConexao[K]) => {
    setDados((d) => ({ ...d, [k]: v }));
    setTeste(null);
  };

  const testar = useMutation({
    mutationFn: () => apiConexoes.testar({ ...dados, id: conexao?.id }),
    onSuccess: setTeste,
    onError: (e) => setErro(e instanceof ErroApi ? e : new ErroApi(0, 'ERRO', 'Falha ao testar.')),
  });
  const salvar = useMutation({
    mutationFn: () => (conexao ? apiConexoes.salvar(conexao.id, dados) : apiConexoes.criar(dados)),
    onSuccess: ({ conexao: c }) => aoConcluir(`Ambiente "${c.nomeAmbiente}" salvo.${c.ativo ? ' Ele é o ambiente ativo.' : ''}`),
    onError: (e) => setErro(e instanceof ErroApi ? e : new ErroApi(0, 'ERRO', 'Não foi possível salvar.')),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setErro(null);
        salvar.mutate();
      }}
    >
      {erro && !erro.doCampo('senha') && <Aviso tipo="erro">{erro.message}</Aviso>}
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <Campo rotulo="Nome do ambiente" value={dados.nomeAmbiente} onChange={(e) => mudar('nomeAmbiente', e.target.value)} placeholder="Produção" erro={erro?.doCampo('nomeAmbiente')} />
        <Selecao rotulo="Tipo" value={dados.tipo} onChange={(e) => mudar('tipo', e.target.value as TipoAmbiente)} className="w-44">
          <option value="PRODUCAO">Produção</option>
          <option value="HOMOLOGACAO">Homologação</option>
        </Selecao>
      </div>
      <div className="grid grid-cols-[1fr_7rem] gap-3">
        <Campo rotulo="Servidor (host)" value={dados.host} onChange={(e) => mudar('host', e.target.value)} erro={erro?.doCampo('host')} />
        <Campo rotulo="Porta" inputMode="numeric" value={String(dados.porta)} onChange={(e) => mudar('porta', Number(e.target.value.replace(/\D/g, '')) || 0)} erro={erro?.doCampo('porta')} />
      </div>
      <Campo rotulo="Banco" value={dados.banco} onChange={(e) => mudar('banco', e.target.value)} placeholder="cigam" erro={erro?.doCampo('banco')} />
      <div className="grid grid-cols-2 gap-3">
        <Campo rotulo="Usuário" autoComplete="off" value={dados.usuario} onChange={(e) => mudar('usuario', e.target.value)} erro={erro?.doCampo('usuario')} />
        <Campo
          rotulo="Senha"
          type="password"
          autoComplete="new-password"
          value={dados.senha}
          onChange={(e) => mudar('senha', e.target.value)}
          ajuda={conexao?.temSenha ? 'Em branco = manter a senha salva.' : undefined}
          erro={erro?.doCampo('senha')}
        />
      </div>
      <div className="flex flex-wrap gap-5">
        <CaixaMarcar rotulo="Criptografar conexão (encrypt)" checked={dados.encrypt} onChange={(e) => mudar('encrypt', e.target.checked)} />
        <CaixaMarcar rotulo="Confiar no certificado do servidor" checked={dados.trustServerCertificate} onChange={(e) => mudar('trustServerCertificate', e.target.checked)} />
      </div>
      {teste &&
        (teste.ok ? (
          <Aviso tipo="ok">
            Conectou em {teste.ms} ms (SQL Server {teste.versao}). {formatarInteiro(teste.tabelasPreco)} tabelas de preço na GETPRECO.
          </Aviso>
        ) : (
          <Aviso tipo="erro">{teste.mensagem}</Aviso>
        ))}
      <div className="flex justify-between gap-2 border-t border-linha-suave pt-4">
        <Botao variante="secundario" carregando={testar.isPending} onClick={() => testar.mutate()}>
          Testar conexão
        </Botao>
        <Botao type="submit" variante="principal" carregando={salvar.isPending}>
          Salvar
        </Botao>
      </div>
    </form>
  );
}
